/**
 * Deal List page (`/deals`).
 *
 * Wave B page 2 of 3 - the main hub where authenticated users browse
 * every escrow they participate in (as buyer or seller). Reuses the Wave
 * A foundation:
 *   - `deals.listDeals` for fetching (single backend call, no role
 *     parameter so the server merges/dedupes both roles for us).
 *   - Telegram MainButton for the "New Deal" CTA.
 *   - `useT()` for every user-facing string (no hardcoded copy).
 *   - `nanoToTon` so monetary values stay bigint until the very last
 *     moment they hit the DOM.
 *
 * Filtering is intentionally client-side. The list is small (max 50)
 * and re-fetching on every dropdown change would burn the rate-limit
 * budget for no UX win.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/router';
import { Plus, RefreshCw, AlertCircle, ShieldAlert, User } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { deals as dealsApi, users as usersApi } from '@/lib/api';
import type { Deal, DealStatus } from '@/lib/api/types';
import { ApiError } from '@/lib/api/errors';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { nanoToTon } from '@/lib/ton/format';
import { formatRelativeTime, formatRemainingShort, truncateText } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { useMainButton } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';

// ─── Filter model ───────────────────────────────────────────

type RoleFilter = 'all' | 'buyer' | 'seller';
type StatusFilter = 'all' | 'active' | 'completed' | 'disputed' | 'cancelled';

const STATUS_GROUPS: Record<Exclude<StatusFilter, 'all'>, readonly DealStatus[]> = {
  active: ['created', 'funded', 'active', 'delivered'],
  completed: ['completed'],
  disputed: ['disputed', 'resolved'],
  cancelled: ['cancelled', 'expired', 'refunded'],
};

type BadgeVariant =
  | 'default'
  | 'gold'
  | 'success'
  | 'warning'
  | 'danger'
  | 'outline'
  | 'muted';

const STATUS_BADGE_VARIANT: Record<DealStatus, BadgeVariant> = {
  created: 'gold',
  funded: 'gold',
  active: 'default',
  delivered: 'default',
  completed: 'success',
  disputed: 'danger',
  resolved: 'danger',
  cancelled: 'muted',
  expired: 'muted',
  refunded: 'muted',
};

// ─── Helpers ────────────────────────────────────────────────

/** Format `nano` (bigint) into a compact "1.23 TON" display string. */
function formatTonDisplay(nano: bigint): string {
  return `${nanoToTon(nano, 2)} TON`;
}

/**
 * The backend mirrors deals into both roles. With no current-user id
 * exposed on the frontend yet (Wave A skipped `users.getMe` for the list
 * view), the safest heuristic is to derive role membership from whether
 * the deal arrived via the buyer or seller index. Since `listByUser`
 * merges and dedupes, we cannot reliably split client-side without an
 * extra `me` lookup. As a pragmatic compromise we tag every deal with
 * BOTH roles when filtering by 'all', and when the user picks a specific
 * role we filter by whichever side the current user actually owns. To
 * achieve that we lazily load the current user's id once on mount.
 */

// ─── Skeleton row ───────────────────────────────────────────

function DealRowSkeleton(): JSX.Element {
  return (
    <Card className="flex items-center gap-4">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-3 w-12" />
      </div>
    </Card>
  );
}

// ─── Deal row ───────────────────────────────────────────────

/** Deal statuses where the row should show "X kaldi" instead of "Y ago". */
const NON_TERMINAL_STATUSES: ReadonlySet<DealStatus> = new Set<DealStatus>([
  'created',
  'funded',
  'active',
  'delivered',
]);

/**
 * Pick the deadline that's currently "running" for a deal, based on its
 * FSM state. Returns null for terminal states (completed/cancelled/etc.)
 * and for deals where the relevant deadline column is missing.
 */
function activeDeadlineForDeal(deal: Deal): string | null {
  if (deal.status === 'created') return deal.fundingDeadline;
  if (deal.status === 'funded' || deal.status === 'active') {
    return deal.deliveryDeadline ?? null;
  }
  if (deal.status === 'delivered') return deal.reviewDeadline ?? null;
  return null;
}

interface DealRowProps {
  deal: Deal;
  role: 'buyer' | 'seller';
  /** Live "now" timestamp from the parent — drives the countdown ticker. */
  now: number;
  onSelect: (shortId: string) => void;
}

function DealRow({ deal, role, now, onSelect }: DealRowProps): JSX.Element {
  const t = useT();

  const amountText = useMemo(() => {
    try {
      return formatTonDisplay(BigInt(deal.amount));
    } catch {
      // Defensive: backend should never send a non-numeric string but
      // we'd rather show a placeholder than crash the whole list.
      return '— TON';
    }
  }, [deal.amount]);

  const statusVariant = STATUS_BADGE_VARIANT[deal.status];
  const statusLabel = t(`dealList.status.${deal.status}` as const);
  const roleLabel = t(`dealList.role.${role}` as const);
  const initial = role === 'buyer' ? 'B' : 'S';

  /**
   * Footer label, derived from the deal's lifecycle state:
   *
   *   - Non-terminal (created/funded/active/delivered) → live countdown
   *     against the relevant deadline. Colour-staged 4 ways (green / yellow
   *     / orange / red) so the user can scan a long list and instantly
   *     spot the rows that need attention.
   *   - Terminal (completed/cancelled/expired/refunded/disputed/resolved)
   *     → falls back to the existing "X ago" relative timestamp.
   */
  const footer = useMemo(() => {
    if (NON_TERMINAL_STATUSES.has(deal.status)) {
      const deadline = activeDeadlineForDeal(deal);
      if (deadline) {
        const { text, remainingMs, expired } = formatRemainingShort(deadline, now);
        if (expired) {
          return { text, toneClass: 'text-rose-400' };
        }
        // 4-stage colour palette mirrors DeadlineBar so the list and the
        // detail page agree on what "almost out of time" means.
        const totalMs =
          new Date(deadline).getTime() - new Date(deal.createdAt).getTime();
        const pct = totalMs > 0 ? (remainingMs / totalMs) * 100 : 100;
        const toneClass =
          pct > 60
            ? 'text-emerald-300'
            : pct > 40
              ? 'text-yellow-300'
              : pct > 20
                ? 'text-orange-400'
                : 'text-rose-400';
        return { text, toneClass };
      }
    }
    return {
      text: formatRelativeTime(deal.createdAt),
      toneClass: 'text-text-muted',
    };
  }, [deal, now]);

  const handleClick = useCallback(() => {
    haptics.light();
    onSelect(deal.shortId);
  }, [deal.shortId, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`${deal.title} - ${statusLabel}`}
      className={cn(
        'group block w-full rounded-lg text-left',
        'transition-transform duration-150 active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
        'focus-visible:ring-offset-bg-primary',
      )}
    >
      <Card className="flex items-center gap-4 group-hover:border-accent-purple/40">
        <div
          aria-hidden
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
            'bg-gradient-to-br from-accent-purple/30 to-accent-gold/20',
            'text-base font-bold text-text-primary',
          )}
        >
          {initial}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-text-primary">
              {truncateText(deal.title, 40)}
            </p>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
            <span className="font-mono">#{deal.shortId}</span>
            <span aria-hidden>·</span>
            <span>{roleLabel}</span>
          </div>
          <p className="mt-1 text-sm font-semibold text-accent-gold">
            {amountText}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Badge variant={statusVariant}>{statusLabel}</Badge>
          <span
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wide tabular-nums',
              footer.toneClass,
            )}
          >
            {footer.text}
          </span>
        </div>
      </Card>
    </button>
  );
}

// ─── Filter chips ───────────────────────────────────────────

interface FilterChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function FilterChip({ active, label, onClick }: FilterChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        haptics.selectionChanged();
        onClick();
      }}
      className={cn(
        'rounded-full px-3 py-1 text-xs font-medium transition-colors',
        'border focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
        'focus-visible:ring-offset-bg-primary',
        active
          ? 'border-accent-purple/60 bg-accent-purple/15 text-accent-purple'
          : 'border-border bg-bg-secondary/50 text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );
}

// ─── Page ───────────────────────────────────────────────────

interface DealListState {
  status: 'idle' | 'loading' | 'success' | 'error';
  deals: readonly Deal[];
  error: string | null;
}

const INITIAL_STATE: DealListState = {
  status: 'loading',
  deals: [],
  error: null,
};

export function DealListPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const userSnapshot = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);

  const [state, setState] = useState<DealListState>(INITIAL_STATE);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // ── Resolve current-user id ─────────────────────────────
  // The user snapshot is normally seeded by Onboarding, but a deep-link
  // straight into /deals can race that. Lazily fetch /users/me when the
  // snapshot isn't available so the role-per-deal computation below has
  // a real id to compare against (NOT the filter selection — see below).
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    userSnapshot?.id ?? null,
  );

  useEffect(() => {
    if (currentUserId) return;
    if (userSnapshot?.id) {
      setCurrentUserId(userSnapshot.id);
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const { user } = await usersApi.getMe();
        if (cancelled) return;
        setCurrentUserId(user.id);
        setUser({
          id: user.id,
          telegramId: user.telegramId,
          username: user.username,
          tonAddress: user.tonAddress,
          reputationScore: user.reputationScore,
          role: user.role,
          isBanned: user.isBanned,
        });
      } catch {
        // Page can still render with role='unknown' for each row; the
        // filter chips just won't gate anything until we know the id.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, userSnapshot, setUser]);

  // Live "now" tick used by the per-row countdown footers. We update once
  // every 30 seconds — fine-grained enough that the user sees the minute
  // counter change while watching the list, cheap enough that idle tabs
  // don't burn CPU. The tick is only armed when at least one row is in
  // a non-terminal state (no point ticking a list of resolved deals).
  const [now, setNow] = useState<number>(() => Date.now());
  const hasLiveDeal = useMemo(() => {
    if (state.status !== 'success') return false;
    return state.deals.some((d) => NON_TERMINAL_STATUSES.has(d.status));
  }, [state]);

  useEffect(() => {
    if (!hasLiveDeal) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [hasLiveDeal]);

  const isOperator =
    userSnapshot?.role === 'admin' || userSnapshot?.role === 'arbiter';

  // ── Data loader ─────────────────────────────────────────
  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const response = await dealsApi.listDeals({ limit: 50 });
      if (signal?.aborted) return;
      setState({
        status: 'success',
        deals: response.deals,
        error: null,
      });
    } catch (err) {
      if (signal?.aborted) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      setState({ status: 'error', deals: [], error: message });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // ── Telegram MainButton: "New Deal" ─────────────────────
  const goToNewDeal = useCallback(() => {
    haptics.medium();
    navigate('/deals/new');
  }, [navigate]);

  const mainButtonConfig = useMemo(
    () => ({
      text: t('dealList.newDeal'),
      onClick: goToNewDeal,
    }),
    [t, goToNewDeal],
  );

  useMainButton(mainButtonConfig);

  // ── Manual refresh ──────────────────────────────────────
  const handleRefresh = useCallback(() => {
    haptics.light();
    void load();
  }, [load]);

  // ── Card click ──────────────────────────────────────────
  const handleSelect = useCallback(
    (shortId: string): void => {
      navigate(`/deals/${shortId}`);
    },
    [navigate],
  );

  /**
   * Resolve the authenticated user's actual role for a given deal by
   * comparing party UUIDs. Returns `null` when we don't yet know the
   * current user id (snapshot not hydrated AND `getMe` still in flight)
   * — callers should treat that as "render conservatively, don't filter".
   *
   * The 0xCent backend never lets a user be both buyer AND seller on the
   * same deal (DealService.create rejects self-dealing), so this is a
   * straight UUID match — no ambiguous case.
   */
  const roleForDeal = useCallback(
    (deal: Deal): 'buyer' | 'seller' | null => {
      if (!currentUserId) return null;
      if (deal.buyerId === currentUserId) return 'buyer';
      if (deal.sellerId === currentUserId) return 'seller';
      // Defensive: shouldn't happen because backend only returns deals
      // the caller participates in. If it does, fall back to 'buyer' so
      // the row still renders something visible.
      return 'buyer';
    },
    [currentUserId],
  );

  // ── Filtered list ───────────────────────────────────────
  const filteredDeals = useMemo<readonly Deal[]>(() => {
    let next = state.deals;

    if (statusFilter !== 'all') {
      const allowed = new Set<DealStatus>(STATUS_GROUPS[statusFilter]);
      next = next.filter((d) => allowed.has(d.status));
    }

    // Real role filtering: only kicks in once we know the current user id.
    // Until then we leave the list untouched so the user isn't staring at
    // an empty screen during the brief `getMe` round-trip.
    if (roleFilter !== 'all' && currentUserId) {
      next = next.filter((d) => roleForDeal(d) === roleFilter);
    }

    return [...next].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [state.deals, statusFilter, roleFilter, currentUserId, roleForDeal]);

  // ── Render helpers ──────────────────────────────────────
  const isFiltered = roleFilter !== 'all' || statusFilter !== 'all';

  const clearFilters = useCallback(() => {
    haptics.light();
    setRoleFilter('all');
    setStatusFilter('all');
  }, []);

  const renderRoleFilters = (): JSX.Element => (
    <div
      role="tablist"
      aria-label={t('dealList.filter.role.all')}
      className="flex items-center gap-2"
    >
      <FilterChip
        active={roleFilter === 'all'}
        label={t('dealList.filter.role.all')}
        onClick={() => setRoleFilter('all')}
      />
      <FilterChip
        active={roleFilter === 'buyer'}
        label={t('dealList.filter.role.buyer')}
        onClick={() => setRoleFilter('buyer')}
      />
      <FilterChip
        active={roleFilter === 'seller'}
        label={t('dealList.filter.role.seller')}
        onClick={() => setRoleFilter('seller')}
      />
    </div>
  );

  const renderStatusFilters = (): JSX.Element => (
    <div
      role="tablist"
      aria-label={t('dealList.filter.status.all')}
      className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1"
    >
      <FilterChip
        active={statusFilter === 'all'}
        label={t('dealList.filter.status.all')}
        onClick={() => setStatusFilter('all')}
      />
      <FilterChip
        active={statusFilter === 'active'}
        label={t('dealList.filter.status.active')}
        onClick={() => setStatusFilter('active')}
      />
      <FilterChip
        active={statusFilter === 'completed'}
        label={t('dealList.filter.status.completed')}
        onClick={() => setStatusFilter('completed')}
      />
      <FilterChip
        active={statusFilter === 'disputed'}
        label={t('dealList.filter.status.disputed')}
        onClick={() => setStatusFilter('disputed')}
      />
      <FilterChip
        active={statusFilter === 'cancelled'}
        label={t('dealList.filter.status.cancelled')}
        onClick={() => setStatusFilter('cancelled')}
      />
    </div>
  );

  const renderBody = (): JSX.Element => {
    if (state.status === 'loading') {
      return (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <DealRowSkeleton key={idx} />
          ))}
        </div>
      );
    }

    if (state.status === 'error') {
      return (
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="rounded-full bg-danger/15 p-3 text-danger">
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-text-primary">
              {t('dealList.error.title')}
            </h3>
            {state.error && (
              <p className="max-w-sm text-xs text-text-muted">{state.error}</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      );
    }

    if (filteredDeals.length === 0) {
      if (isFiltered) {
        return (
          <EmptyState
            title={t('dealList.filtered.empty')}
            action={
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t('dealList.filtered.clear')}
              </Button>
            }
          />
        );
      }
      return (
        <EmptyState
          title={t('dealList.empty.title')}
          description={t('dealList.empty.body')}
          action={
            <Button variant="gold" size="sm" onClick={goToNewDeal}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('dealList.empty.cta')}
            </Button>
          }
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {filteredDeals.map((deal) => (
          <DealRow
            key={deal.id}
            deal={deal}
            // Real role from UUID match — never derived from the filter.
            // Falls back to 'buyer' until `currentUserId` lands so the
            // first paint after a deep-link doesn't blank the avatar.
            role={roleForDeal(deal) ?? 'buyer'}
            now={now}
            onSelect={handleSelect}
          />
        ))}
      </div>
    );
  };

  return (
    <AppShell>
      <PageHeader
        title={t('dealList.title')}
        action={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('dealList.refresh')}
              onClick={handleRefresh}
              disabled={state.status === 'loading'}
            >
              <RefreshCw
                className={cn(
                  'h-5 w-5',
                  state.status === 'loading' && 'animate-spin',
                )}
                aria-hidden
              />
            </Button>
            {isOperator && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('admin.nav')}
                onClick={() => {
                  haptics.light();
                  navigate(ROUTES.adminDisputes);
                }}
              >
                <ShieldAlert className="h-5 w-5 text-accent-gold" aria-hidden />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Profile"
              onClick={() => {
                haptics.light();
                navigate(ROUTES.profile);
              }}
            >
              <User className="h-5 w-5" aria-hidden />
            </Button>
          </div>
        }
      />

      <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-2 bg-bg-primary/80 px-4 pb-2 pt-1 backdrop-blur">
        {renderRoleFilters()}
        {renderStatusFilters()}
      </div>

      {renderBody()}
    </AppShell>
  );
}
